<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :header="t('campaignsBulkList.actions.suspendDialog.header')"
    :style="{ width: '450px' }"
  >
    <div class="p-fluid">
      <p>{{ t('campaignsBulkList.actions.suspendDialog.message') }}</p>
    </div>
    <template #footer>
      <SpmButton
        :label="t('no')"
        icon="far fa-times"
        class-style="p-button p-button-secondary"
        @click="close"
      />
      <SpmButton
        :label="t('yes')"
        icon="far fa-check"
        class-style="p-button p-button-danger"
        :loading="isLoading"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { setRefreshScenariosList } from '@/composables/AutomatedScenarios';
import { getBulkCampaignStatus } from '@/composables/workflows/CampaignBulk';
import { updateBulkStatus } from '@/composables/flows/useFlowApi';
import { CampaignsBulkStatusEnum } from '@/types';
import type { CampaignActionPayload, SuspendCampaignResult } from './types';

export default defineComponent({
  name: 'SuspendBulkCampaignAction',

  components: {
    Dialog,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();

    // Internal state
    const isVisible = ref(false);
    const isLoading = ref(false);
    const currentPayload = ref<CampaignActionPayload | null>(null);

    /**
     * Open the dialog with the given payload
     */
    const open = (payload: CampaignActionPayload) => {
      currentPayload.value = payload;
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentPayload.value = null;
      isLoading.value = false;
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = async () => {
      if (!currentPayload.value) return;

      isLoading.value = true;

      try {
        // Check if status hasn't changed on server and is still SENDING
        const currentStatus = await getBulkCampaignStatus(currentPayload.value.campaignId);
        if (!currentStatus
          || currentStatus !== currentPayload.value.campaignStatus
          || currentStatus !== CampaignsBulkStatusEnum.SENDING) {
          await showToastError(t('errorMessages.GENERIC_ERROR'));
          setRefreshScenariosList(true);
          close();
          return;
        }

        await updateBulkStatus(currentPayload.value.campaignId, CampaignsBulkStatusEnum.SUSPENDED_BY_USER);
        await showToastSuccess(t('campaignsBulkList.actions.suspendSuccess'));
        setRefreshScenariosList(true);

        const result: SuspendCampaignResult = {
          campaignId: currentPayload.value.campaignId,
        };
        emit('confirm', result);
        close();
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : t('errorMessages.GENERIC_ERROR');
        await showToastError(errorMessage);
      } finally {
        isLoading.value = false;
      }
    };

    // Expose open method for parent component
    expose({ open });

    return {
      t,
      isVisible,
      isLoading,
      close,
      confirm,
    };
  },
});
</script>
